// Adaptér: strom Magic menu (po `desktop/menu-ready`) → view-model sdílené komponenty `MainMenu`.
//
// - top-level `sub` → MenuSection (Obecné, Inventář, …)
// - vnořený `sub` → MenuGroupNode
// - `form`/`click` → MenuItemNode (nese `key` GlobalName a raw `menuItemId` jako dispatch token)
// - `separator` → line, `space` → space (ZACHOVÁNO uvnitř dropdownu — oprava proti dřívějšímu flatten)
// - `text` → vynecháno (v1)
// Prázdné suby (bez listů/skupin) se prořezávají; krajní oddělovače se ořezávají.

import type { MenuNode, MenuSection } from '@/shared/ui';
import type { MagicMenuItem } from './types';

function hasContent(nodes: readonly MenuNode[]): boolean {
  return nodes.some((n) => n.t === 'item' || n.t === 'group');
}

function trimSeparators(nodes: MenuNode[]): MenuNode[] {
  let start = 0;
  let end = nodes.length;
  while (start < end && (nodes[start].t === 'line' || nodes[start].t === 'space')) start++;
  while (end > start && (nodes[end - 1].t === 'line' || nodes[end - 1].t === 'space')) end--;
  return nodes.slice(start, end);
}

function convertNode(item: MagicMenuItem): MenuNode | null {
  switch (item.kind) {
    case 'separator':
      return { t: 'line' };
    case 'space':
      return { t: 'space' };
    case 'text':
      return null;
    case 'sub': {
      const items = convertNodes(item.children ?? []);
      if (!hasContent(items)) return null;
      return {
        t: 'group',
        id: item.menuItemId,
        stableId: item.stableId,
        title: item.title ?? '',
        key: item.key,
        items,
      };
    }
    case 'link':
      return {
        t: 'item',
        id: item.menuItemId,
        stableId: item.stableId,
        title: item.title ?? '',
        key: item.key,
        origin: 'legacy',
        url: item.url,
      };
    case 'form':
    case 'click':
    default:
      return {
        t: 'item',
        id: item.menuItemId,
        stableId: item.stableId,
        title: item.title ?? '',
        key: item.key,
        origin: 'legacy',
      };
  }
}

function convertNodes(items: readonly MagicMenuItem[]): MenuNode[] {
  const out: MenuNode[] = [];
  for (const it of items) {
    const n = convertNode(it);
    if (n) out.push(n);
  }
  return trimSeparators(out);
}

/** Převede strom Magic menu na pole sekcí pro `MainMenu`. */
export function toMenuViewModel(items: readonly MagicMenuItem[]): MenuSection[] {
  const sections: MenuSection[] = [];
  for (const it of items) {
    if (it.kind !== 'sub') continue; // top-level musí být sekce (sub)
    const children = convertNodes(it.children ?? []);
    if (!hasContent(children)) continue;
    // `id` (React-key sekce) odvozen ze stabilního `stableId` (přežije reload/změnu kultury);
    // fallback na ephemerální `menuItemId`, když Magic `stableId` neposílá.
    sections.push({
      id: `magic-${it.stableId ?? it.menuItemId}`,
      stableId: it.stableId,
      label: it.title ?? '',
      items: children,
    });
  }
  return sections;
}
